import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AddCircleIcon, Download02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CheckHistoryTypes,
  HistoryTypeGroupOptions,
  historyTypesForGroups,
} from 'constants/check-history-types';
import { emailTemplateTypeForHistoryEntry } from 'constants/history-email-template-types';
import { VIEW_LINK_TYPES, historyTypeInSet } from 'constants/history-view-link-types';
import { exportHistoryToCSV, getHistoryEntrySearchText } from 'helpers/history-export';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { AddHistoryModal } from './components/AddHistoryModal/AddHistoryModal';
import { Button } from 'components/FormComponents';
import { HistoryDetailsPanel } from 'components/HistoryDetailsPanel/HistoryDetailsPanel';
import { HistoryEmailTemplateModal } from 'components/HistoryEmailTemplateModal/HistoryEmailTemplateModal';
import { HistoryRow } from 'components/HistoryRow/HistoryRow';
import { Table } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import {
  useGetCandidateHistoryQuery,
  useUpdateCandidateHistoryEntryMutation,
} from 'api/candidates-api';
import { useGetCompanyUsersQuery } from 'api/user-api';

const STORAGE_KEY_PREFIX = 'checkmate_history_pinned_';

function getPinnedIds(candidateId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + candidateId);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function persistPinnedIds(candidateId, ids) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + candidateId, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

/** Same membership for two Sets of ids — avoids new Set reference when merge is a no-op (keeps callbacks stable). */
function sameIdSet(a, b) {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

// Entry types that have order form data worth showing in the details panel — these need
// a resolved check/check_type to cross-reference client_fields.
const DETAILS_TYPES = new Set([
  CheckHistoryTypes.NewCheck,
  CheckHistoryTypes.BackgroundCheckSubmitted,
  CheckHistoryTypes.BackgroundCheckProcessing,
  CheckHistoryTypes.BackgroundCheckCompleted,
  CheckHistoryTypes.BackgroundCheckUpdated,
  CheckHistoryTypes.SubCheckCompleted,
]);

// Field-change types carry a self-contained {field, from, to} (or consolidated) payload in
// entry.message, so the details panel can render them without a check_type — these can be
// changes to a candidate (not scoped to any single check) as well as to a referee.
const SELF_CONTAINED_DETAILS_TYPES = new Set([
  CheckHistoryTypes.UpdatedEmail,
  CheckHistoryTypes.UpdatedContactNumber,
  CheckHistoryTypes.UpdatedRefereeDetails,
  CheckHistoryTypes.CandidateUpdatedRefereeDetails,
  CheckHistoryTypes.CandidateReplacedReferee,
  CheckHistoryTypes.UpdatedCandidateDetails,
  CheckHistoryTypes.UpdatedDepartment,
  CheckHistoryTypes.UpdatedJobRole,
  CheckHistoryTypes.UpdatedCandidateTeam,
  CheckHistoryTypes.TransferUserAssignment,
  CheckHistoryTypes.UpdatedCandidateNotificationList,
]);

function hasDetailsToShow(entry) {
  return (
    (historyTypeInSet(DETAILS_TYPES, entry.type) && entry.check_type) ||
    historyTypeInSet(SELF_CONTAINED_DETAILS_TYPES, entry.type)
  );
}

function parseTimeMs(value) {
  if (value == null || value === '') return null;
  const n = new Date(value).getTime();
  return Number.isFinite(n) ? n : null;
}

/**
 * Legacy history rows may omit candidate_check_id. If several checks share the same type,
 * choose the one whose lifecycle timestamps are closest to the history row's created_at.
 */
function pickCheckForHistoryEntry(checksOfType, historyEntry) {
  if (!checksOfType.length) return undefined;
  if (checksOfType.length === 1) return checksOfType[0];

  const entryMs = parseTimeMs(historyEntry?.created_at);
  const anchorFields = ['created_at', 'submitted_at', 'completed_at', 'job_at'];

  let best = checksOfType[0];
  let bestScore = Infinity;

  for (const c of checksOfType) {
    let minDelta = Infinity;
    if (entryMs != null) {
      for (const f of anchorFields) {
        const t = parseTimeMs(c[f]);
        if (t != null) minDelta = Math.min(minDelta, Math.abs(t - entryMs));
      }
    }
    const better =
      minDelta < bestScore || (minDelta === bestScore && Number(c.id) > Number(best.id));
    if (better) {
      bestScore = minDelta;
      best = c;
    }
  }

  return best;
}

function resolveCheckForDetailsEntry(detailsEntry, checks) {
  if (!detailsEntry) return undefined;
  const byId = checks.find(
    c =>
      c &&
      detailsEntry.candidate_check_id != null &&
      String(c.id) === String(detailsEntry.candidate_check_id),
  );
  if (byId) return byId;

  const sameType = checks.filter(c => c && c.type === detailsEntry.check_type);
  return pickCheckForHistoryEntry(sameType, detailsEntry);
}

export const History = ({
  company,
  candidate,
  brand,
  referees,
  user,
  companyChecks,
  checks = [],
  targetNewRecord,
  readOnly = false,
  createRecord = () => {},
  onCandidateRequest = () => {},
  onRefereeRequest = () => {},
  setLoading = () => {},
}) => {
  const [entries, setEntries] = useState([]);
  const [addNoteModal, setAddNoteModal] = useState(false);
  const [exitingPinnedIds, setExitingPinnedIds] = useState(new Set());
  const [pinnedIds, setPinnedIds] = useState(() => getPinnedIds(candidate.id));
  const [detailsEntry, setDetailsEntry] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [emailTemplateEntry, setEmailTemplateEntry] = useState(null);
  const [emailTemplateModalOpen, setEmailTemplateModalOpen] = useState(false);
  const [filterParams, setFilterParams] = useState({});
  /** History IDs optimistically unpinned; excluded from API merge until server returns pinned: false */
  const optimisticUnpinnedIdsRef = useRef(new Set());

  const { data: companyUsers } = useGetCompanyUsersQuery(company.id);

  // Sync pinned IDs from localStorage when candidate changes (also covered by key={candidate.id} on parents)
  useEffect(() => {
    optimisticUnpinnedIdsRef.current = new Set();
    setPinnedIds(getPinnedIds(candidate.id));
  }, [candidate.id]);

  // Persist pinned set after state updates — keep side effects out of setPinnedIds updaters (Strict Mode-safe)
  useEffect(() => {
    persistPinnedIds(candidate.id, pinnedIds);
  }, [candidate.id, pinnedIds]);

  // Filter out the current user from the company users so they can't tag themselves in history record
  const filteredCompanyUsers = useMemo(
    () => companyUsers?.filter(companyUser => companyUser && companyUser.id !== user?.id) ?? [],
    [companyUsers, user?.id],
  );

  const {
    data: entriesData,
    isLoading: isLoadingRecords,
    refetch: refetchHistory,
  } = useGetCandidateHistoryQuery({
    id: candidate.id,
    params: { internal: false },
  });

  const [updateHistoryEntry] = useUpdateCandidateHistoryEntryMutation();
  const navigate = useNavigate();

  // API is source of truth when it reports pinned; merge with optimistic pins so a refetch from an
  // earlier pin mutation doesn't overwrite a later optimistic pin before its refetch completes.
  // Also respect optimistic unpins so stale refetches (e.g. refetchOnFocus) don't re-add pins before PUT completes.
  useEffect(() => {
    if (entriesData === undefined) return;
    for (const e of entriesData) {
      if (e && e.id != null && optimisticUnpinnedIdsRef.current.has(e.id) && !e.pinned) {
        optimisticUnpinnedIdsRef.current.delete(e.id);
      }
    }
    const fromApi = new Set(entriesData.filter(e => e && e.pinned).map(e => e.id));
    setPinnedIds(prev => {
      const merged = new Set(fromApi);
      for (const id of optimisticUnpinnedIdsRef.current) {
        merged.delete(id);
      }
      for (const e of entriesData) {
        if (e && e.id != null && prev.has(e.id) && !e.pinned) {
          merged.add(e.id);
        }
      }
      return sameIdSet(prev, merged) ? prev : merged;
    });
  }, [entriesData, candidate.id]);

  const handleView = useCallback(
    entry => {
      // Navigate to the candidate tab most relevant to this history entry
      const tab = entry?.check_type ? 'background_checks' : 'candidate';
      navigate(`/check/${candidate.id}/${tab}`);
    },
    [candidate.id, navigate],
  );

  const handleDetails = useCallback(entry => {
    setDetailsEntry(entry);
    setDetailsOpen(true);
  }, []);
  const handleCloseDetails = useCallback(() => setDetailsOpen(false), []);
  const handleDetailsExited = useCallback(() => setDetailsEntry(null), []);

  const handleViewEmailTemplate = useCallback(entry => {
    setEmailTemplateEntry(entry);
    setEmailTemplateModalOpen(true);
  }, []);
  const handleCloseEmailTemplateModal = useCallback(() => setEmailTemplateModalOpen(false), []);

  // Resolve the check object + companyCheck for the details panel.
  const detailsPanelData = useMemo(() => {
    if (!detailsEntry) return {};
    const check = resolveCheckForDetailsEntry(detailsEntry, checks);
    const companyCheck = companyChecks?.find(cc => cc && cc.type === detailsEntry.check_type);
    const checkHuman = companyCheck?.check_type?.title ?? null;
    return { check, companyCheck, checkHuman };
  }, [detailsEntry, checks, companyChecks]);

  useEffect(() => {
    if (entriesData) setEntries(entriesData);
  }, [entriesData]);

  useEffect(() => {
    setLoading(isLoadingRecords);
  }, [isLoadingRecords]);

  useEffect(() => {
    if (targetNewRecord) {
      setAddNoteModal(true);
    }
  }, [targetNewRecord]);

  const handleAddRecord = async data => {
    setLoading(true);
    try {
      const result = await createRecord({ id: candidate.id, data }).unwrap();
      setEntries(prev => [result, ...prev]);
    } catch {
      toast.error('Could not add history record — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setAddNoteModal(false);
    // Navigate to the base history path without the 'new' target
    navigate(`/check/${candidate.id}/history`);
  };

  const searchContext = useMemo(
    () => ({ referees, companyUsers, companyChecks, company }),
    [referees, companyUsers, companyChecks, company],
  );

  const hasActiveFilters = Boolean(
    filterParams.search || filterParams.type?.length || filterParams.created_at?.length,
  );

  const filteredEntries = useMemo(() => {
    const searchQuery = (filterParams.search || '').trim().toLowerCase();
    const typeSet = historyTypesForGroups(filterParams.type);
    const dateRange = filterParams.created_at;
    const rangeStart = dateRange?.[0] ? moment(dateRange[0], 'YYYY-MM-DD').startOf('day') : null;
    const rangeEnd = dateRange?.[1] ? moment(dateRange[1], 'YYYY-MM-DD').endOf('day') : null;

    return entries.filter(entry => {
      if (!entry) return false;

      if (typeSet && !typeSet.has(Number(entry.type))) return false;

      if (rangeStart && rangeEnd) {
        const createdAt = moment(entry.created_at);
        if (!createdAt.isValid() || createdAt.isBefore(rangeStart) || createdAt.isAfter(rangeEnd)) {
          return false;
        }
      }

      if (searchQuery) {
        const haystack = getHistoryEntrySearchText(entry, candidate, searchContext);
        if (!haystack.includes(searchQuery)) return false;
      }

      return true;
    });
  }, [entries, filterParams, candidate, searchContext]);

  const handleDownload = () => {
    if (!filteredEntries?.length) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            type="info"
            title={isLoadingRecords ? 'History is still loading' : 'Nothing to export'}
            message={
              isLoadingRecords
                ? 'Please wait for history to finish loading, then try again.'
                : hasActiveFilters
                  ? 'No history records match your current search or filters.'
                  : 'There are no history records for this candidate yet.'
            }
          />
        ),
        { duration: 5000 },
      );
      return;
    }

    const { descriptionFailureCount } = exportHistoryToCSV(filteredEntries, candidate, {
      referees,
      companyUsers,
      companyChecks,
      company,
    });

    if (descriptionFailureCount > 0) {
      const rowLabel = descriptionFailureCount === 1 ? 'row' : 'rows';
      toast.custom(
        t => (
          <CustomToast
            t={t}
            type="warning"
            title="CSV exported with missing descriptions"
            message={`${descriptionFailureCount} ${rowLabel} could not be fully described; those cells use a placeholder.`}
          />
        ),
        { duration: 6000 },
      );
    }
  };

  const handleTogglePin = useCallback(
    entry => {
      const isPinned = pinnedIds.has(entry.id);
      const newPinned = !isPinned;

      const performPin = (clearExiting = false) => {
        optimisticUnpinnedIdsRef.current.delete(entry.id);
        if (clearExiting) {
          setExitingPinnedIds(prev => {
            const next = new Set(prev);
            next.delete(entry.id);
            return next;
          });
        }
        updateHistoryEntry({
          candidateId: candidate.id,
          historyId: entry.id,
          data: { pinned: true },
        })
          .unwrap()
          .then(() => refetchHistory())
          .catch(() => {
            toast.error('Could not pin — please try again.');
            setPinnedIds(prev => {
              const next = new Set(prev);
              next.delete(entry.id);
              return next;
            });
            refetchHistory();
          });
        setPinnedIds(prev => {
          const next = new Set(prev);
          next.add(entry.id);
          return next;
        });
      };

      if (!newPinned) {
        optimisticUnpinnedIdsRef.current.add(entry.id);
        updateHistoryEntry({
          candidateId: candidate.id,
          historyId: entry.id,
          data: { pinned: false },
        })
          .unwrap()
          .then(() => refetchHistory())
          .catch(() => {
            optimisticUnpinnedIdsRef.current.delete(entry.id);
            toast.error('Could not unpin — please try again.');
            setExitingPinnedIds(prev => {
              const next = new Set(prev);
              next.delete(entry.id);
              return next;
            });
            setPinnedIds(prev => {
              const next = new Set(prev);
              next.add(entry.id);
              return next;
            });
            refetchHistory();
          });
        setExitingPinnedIds(prev => new Set([...prev, entry.id]));
        setPinnedIds(prev => {
          const next = new Set(prev);
          next.delete(entry.id);
          return next;
        });
        setTimeout(() => {
          setExitingPinnedIds(prev => {
            const next = new Set(prev);
            next.delete(entry.id);
            return next;
          });
        }, 280);
        toast.custom(
          t => (
            <CustomToast
              t={t}
              type="success"
              title="Removed from Pinned"
              message="The record was removed from the Pinned section. It still appears in All History below."
              actionLabel="Undo"
              onAction={() => performPin(true)}
            />
          ),
          { duration: 4000 },
        );
      } else {
        performPin(false);
      }
    },
    [candidate.id, pinnedIds, updateHistoryEntry, refetchHistory],
  );

  const pinnedEntries = useMemo(
    () => filteredEntries.filter(e => e && (pinnedIds.has(e.id) || exitingPinnedIds.has(e.id))),
    [filteredEntries, pinnedIds, exitingPinnedIds],
  );

  return (
    <div>
      <HistoryDetailsPanel
        open={detailsOpen}
        onClose={handleCloseDetails}
        onExited={handleDetailsExited}
        entry={detailsEntry}
        check={detailsPanelData.check}
        companyCheck={detailsPanelData.companyCheck}
        checkHuman={detailsPanelData.checkHuman}
        candidate={candidate}
        pinned={detailsEntry ? pinnedIds.has(detailsEntry.id) : false}
        onTogglePin={!readOnly ? handleTogglePin : undefined}
      />
      <HistoryEmailTemplateModal
        visible={emailTemplateModalOpen}
        onClose={handleCloseEmailTemplateModal}
        brand={brand}
        emailType={
          emailTemplateEntry ? emailTemplateTypeForHistoryEntry(emailTemplateEntry.type) : null
        }
        emailTemplateId={emailTemplateEntry?.email_template_id}
      />
      {!readOnly && (
        <AddHistoryModal
          visible={addNoteModal}
          onClose={handleCloseModal}
          company={company}
          candidate={candidate}
          companyChecks={companyChecks}
          user={user}
          companyUsers={filteredCompanyUsers}
          onSave={handleAddRecord}
        />
      )}
      <div className="flex flex-row gap-2 justify-between items-center p-8 border-b border-border-primary bg-neutral-1 rounded-t-2xl">
        <div className="flex flex-col gap-1">
          <div className="text-text-primary text-[18px] font-semibold">History</div>
          <div className="text-text-tertiary text-[14px] font-light">
            Track all activity related to this candidate, including checks, updates, communications,
            and pinned records.
          </div>
        </div>
        <div className="flex flex-row items-center gap-4">
          <Button onClick={handleDownload} type="secondary">
            <HugeiconsIcon icon={Download02Icon} className="mr-2" /> Download CSV
          </Button>
          {!readOnly && (
            <Button onClick={() => setAddNoteModal(true)}>
              <HugeiconsIcon icon={AddCircleIcon} className="mr-2" /> Add History Record
            </Button>
          )}
        </div>
      </div>

      <Table.Context onQueryUpdate={setFilterParams}>
        <div className="p-8">
          <Table.FloatingFilters className="mb-4 bg-surface-primary">
            <Table.FloatingFiltersLeft>
              <Table.Search placeholder="Search history..." />
              <Table.MultiSelectFilter
                title="Type"
                filterName="type"
                type="status"
                useDefault={false}
                useSearchInMultiSelect={true}
                options={HistoryTypeGroupOptions}
              />
              <Table.DateRangeFilter title="Date within" filterName="created_at" />
            </Table.FloatingFiltersLeft>
          </Table.FloatingFilters>

          {isLoadingRecords ? (
            /* Loading skeleton */
            <div className="flex flex-col gap-4">
              <div className="text-neutral-5 text-xl font-semibold tracking-widest uppercase px-2 pb-3">
                All History
              </div>
              {[1, 2, 3, 4].map(i => (
                <div
                  key={i}
                  className="flex flex-row gap-4 items-center p-8 rounded-xl border border-border-primary bg-surface-primary animate-pulse">
                  <div className="w-6 h-6 rounded-full bg-neutral-3 shrink-0" />
                  <div className="flex gap-8 shrink-0">
                    <div className="w-32 h-6 bg-neutral-3 rounded" />
                    <div className="w-20 h-6 bg-neutral-3 rounded" />
                  </div>
                  <div className="flex-1 h-6 bg-neutral-3 rounded ml-8" />
                </div>
              ))}
            </div>
          ) : entries.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-text-secondary text-2xl mb-6">No history yet.</p>
              {!readOnly && (
                <Button onClick={() => setAddNoteModal(true)}>
                  <HugeiconsIcon icon={AddCircleIcon} className="mr-2" /> Add History Record
                </Button>
              )}
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-text-secondary text-2xl mb-2">No matching history records.</p>
              <p className="text-text-tertiary text-xl">
                Try adjusting your search or filters to find what you&apos;re looking for.
              </p>
            </div>
          ) : (
            <>
              {/* PINNED section */}
              {pinnedEntries.length > 0 && (
                <div className="flex flex-col gap-4 mb-8">
                  <div className="text-neutral-5 text-xl font-semibold tracking-widest uppercase px-2 pb-3">
                    Pinned
                  </div>
                  {pinnedEntries.map(entry => (
                    <HistoryRow
                      key={`pinned-${entry.id}`}
                      entry={entry}
                      company={company}
                      companyChecks={companyChecks}
                      candidate={candidate}
                      referees={referees}
                      companyUsers={companyUsers}
                      pinned={true}
                      isPinnedSection={true}
                      isExiting={exitingPinnedIds.has(entry.id)}
                      onTogglePin={!readOnly ? handleTogglePin : undefined}
                      onView={
                        historyTypeInSet(VIEW_LINK_TYPES, entry.type) ? handleView : undefined
                      }
                      onDetails={hasDetailsToShow(entry) ? handleDetails : undefined}
                      onViewEmailTemplate={
                        emailTemplateTypeForHistoryEntry(entry.type)
                          ? handleViewEmailTemplate
                          : undefined
                      }
                      detailsDrawerOpen={!!detailsEntry}
                      onCandidateRequest={onCandidateRequest}
                      onRefereeRequest={onRefereeRequest}
                    />
                  ))}
                </div>
              )}

              {/* ALL HISTORY section */}
              <div className="flex flex-col gap-4">
                <div className="text-neutral-5 text-xl font-semibold tracking-widest uppercase px-2 pb-3">
                  All History
                </div>
                {filteredEntries.map(entry => (
                  <HistoryRow
                    key={entry?.id}
                    entry={entry}
                    company={company}
                    companyChecks={companyChecks}
                    candidate={candidate}
                    referees={referees}
                    companyUsers={companyUsers}
                    pinned={pinnedIds.has(entry.id) || exitingPinnedIds.has(entry.id)}
                    isPinnedSection={false}
                    isExiting={exitingPinnedIds.has(entry.id)}
                    onTogglePin={!readOnly ? handleTogglePin : undefined}
                    onView={historyTypeInSet(VIEW_LINK_TYPES, entry.type) ? handleView : undefined}
                    onDetails={hasDetailsToShow(entry) ? handleDetails : undefined}
                    onViewEmailTemplate={
                      emailTemplateTypeForHistoryEntry(entry.type)
                        ? handleViewEmailTemplate
                        : undefined
                    }
                    detailsDrawerOpen={!!detailsEntry}
                    onCandidateRequest={onCandidateRequest}
                    onRefereeRequest={onRefereeRequest}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Table.Context>
    </div>
  );
};
